import type { IntlShape } from 'react-intl';

import escapeRegExp from 'lodash/escapeRegExp';
import { defineMessages } from 'react-intl';
import { generatePath } from 'react-router';

import type { EntityType } from '~/features/managed-entities/entity-types/types/EntityType';

import { NavLink } from '~/eds/src/types/NavLink';
import { isGroupsSystemEntityType } from '~/features/managed-entities/entity-types/utils/isGroupsSystemEntityType';
import { SavedSearch } from '~/features/search/types/search';
import { FlagType } from '~/flags/flags';
import { getFlag } from '~/flags/useFlag';
import { canUseAutomation } from '~/permissions/canUseAutomation';
import { canUseManagedEntities } from '~/permissions/canUseManagedEntities';
import { canUseWorkflowAdmin } from '~/permissions/canUseWorkflowAdmin';
import { checkIsInternalSuperAdmin } from '~/permissions/checkIsInternalSuperAdmin';
import { checkIsWorkflowOnly } from '~/permissions/checkIsWorkflowOnly';
import { checkIsRbacAdmin } from '~/permissions/rbac/checkIsRbacAdmin';
import { DashboardAndPathname } from '~/redux/api/methods/dashboardV2';
import { RoutePathType } from '~/routing/enums';
import { DashboardTab } from '~/types/Dashboard';
import { User } from '~/types/User';

import { featureFlagIncluded } from './user';

const messages = defineMessages({
  workflowsClmEvolution: {
    id: 'PageHeader.navLinks.workflowsClmEvolution',
    defaultMessage: 'Workflows',
    description: 'Workflows CLM Evolution navigation link',
  },
  workflowsClmEvolutionNewBadge: {
    id: 'PageHeader.navLinks.workflowsClmEvolutionNewBadge',
    defaultMessage: 'New',
    description:
      'Badge shown on the Workflows CLM Evolution navigation link when a client also still has the legacy Workflow navigation link, to distinguish the two',
  },
});

// Both nav entries only coexist for clients transitioning off the legacy
// Workflow experience — net-new clients only ever see one, so there's nothing
// to disambiguate and the badge should stay hidden.
const hasBothWorkflowNavEntries = (
  currentUser: User,
  hasClmEvolutionPhase1?: boolean,
) =>
  !!hasClmEvolutionPhase1 && featureFlagIncluded(currentUser, 'WORKFLOW_DEMO');

interface Permissions {
  documents?: boolean;
  entityGroups?: boolean;
  workflowTickets?: boolean;
  workflowBuilder?: boolean;
  clauseLibrary?: boolean;
  automation?: boolean;
  documentXRay?: boolean;
  documentsDashboard?: boolean;
  workflowsDashboard?: boolean;
  managedEntityParties?: boolean;
}

export const createNavlinks = (
  currentUser: User,
  intl: IntlShape,
  onNavLinkClick: (pathname: string) => void,
  dashboards?: DashboardTab[],
  activeSearch?: SavedSearch,
  hasClauseLibrary?: boolean,
  hasDeprecateAnalyzer?: boolean,
  hasPlaybooks?: boolean,
  activeDashboard?: DashboardAndPathname,
  hasClmEvolutionPhase1?: boolean,
  hasDynamicDataExtraction?: boolean,
  hasHome?: boolean,
  permissions: Permissions = {},
  activePathname = '',
  entityTypes: EntityType[] = [],
) => {
  const navLinks: NavLink[] = [];
  if (checkIsWorkflowOnly(currentUser)) {
    if (featureFlagIncluded(currentUser, 'WORKFLOW_DEMO')) {
      return [
        {
          label: intl.formatMessage({
            id: 'PageHeader.navLinks.workflowTickets',
            defaultMessage: 'Workflows',
            description: 'Workflow navigation link',
          }),
          pathname: RoutePathType.WorkflowTickets,
          children: [],
        },
      ];
    }
    return [];
  }

  if (hasHome) {
    navLinks.push({
      label: intl.formatMessage({
        id: 'PageHeader.navLinks.home',
        defaultMessage: 'Home',
        description: 'Home navigation link',
      }),
      pathname: RoutePathType.HomeSurface,
      children: [],
    });
  }

  if (!featureFlagIncluded(currentUser, 'REVIEWER')) {
    const shouldShowDocumentsNav = permissions['documents'];

    const shouldShowDocumentGroupsSubnav =
      permissions['documents'] && permissions['entityGroups'];

    // Users with `entityGroups` but not `documents` land on the Groups tab directly.
    const groupsEntityType = entityTypes.find(isGroupsSystemEntityType);
    const documentsFallbackPathname = groupsEntityType
      ? generatePath(RoutePathType.DocumentsEntityType, {
          entityTypeId: groupsEntityType.id,
        })
      : RoutePathType.Documents;

    if (shouldShowDocumentsNav || permissions['entityGroups']) {
      navLinks.push({
        label: intl.formatMessage({
          id: 'PageHeader.navLinks.documents',
          defaultMessage: 'Documents',
          description: 'Documents navigation link',
        }),
        pathname: shouldShowDocumentsNav
          ? RoutePathType.Documents
          : documentsFallbackPathname,
        children: shouldShowDocumentGroupsSubnav
          ? createDocumentSubNavLinks(
              currentUser,
              intl,
              entityTypes,
              activePathname,
            )
          : [],
      });
    }

    // temporary, should not be a top level navlink
    // incorporate with Documents
    if (permissions['managedEntityParties']) {
      if (canUseManagedEntities(currentUser)) {
        navLinks.push({
          label: intl.formatMessage({
            id: 'PageHeader.navLinks.parties',
            defaultMessage: 'Parties',
            description: 'Parties navigation link',
          }),
          pathname: RoutePathType.ManagedEntities,
        });
      }
    }

    if (permissions['documents']) {
      navLinks.push({
        label: intl.formatMessage({
          id: 'PageHeader.navLinks.search',
          defaultMessage: 'Search',
          description: 'Search navigation link',
        }),
        pathname: RoutePathType.Search,
        children: createSearchSubNavLinks({
          activeSearch,
          hasDeprecateAnalyzer,
          onNavLinkClick,
          intl,
        }),

        onClick: () => onNavLinkClick(RoutePathType.Search),
      });
    }

    if (
      permissions['documentsDashboard'] ||
      permissions['workflowsDashboard']
    ) {
      navLinks.push({
        label: intl.formatMessage({
          id: 'PageHeader.navLinks.dashboards',
          defaultMessage: 'Dashboards',
          description: 'Dashboards navigation link',
        }),
        pathname: '/dashboards',
        children: createDashboardsSubNavLinks(
          currentUser,
          RoutePathType.Dashboards,
          intl,
          dashboards,
          onNavLinkClick,
          activeDashboard,
        ),
      });
    }

    const workflowPermissionCount = [
      permissions['workflowTickets'],
      permissions['workflowBuilder'],
      permissions['entityGroups'],
    ].filter(Boolean).length;

    if (permissions['workflowTickets'] || permissions['workflowBuilder']) {
      if (featureFlagIncluded(currentUser, 'WORKFLOW_DEMO')) {
        const shouldShowSubNav = workflowPermissionCount > 1;
        navLinks.push({
          label: intl.formatMessage({
            id: 'PageHeader.navLinks.workflow',
            defaultMessage: 'Workflows',
            description: 'Workflow navigation link',
          }),
          pathname: permissions['workflowTickets']
            ? RoutePathType.WorkflowTickets
            : RoutePathType.WorkflowBuilder,
          children: shouldShowSubNav
            ? createWorkflowSubNavLinks(currentUser, intl, permissions)
            : [],
        });
      }
    }
    if (
      (permissions['workflowTickets'] || permissions['workflowBuilder']) &&
      hasClmEvolutionPhase1
    ) {
      const shouldShowSubNav = workflowPermissionCount > 1;
      const isClmEvolutionDetailPage =
        new RegExp(
          `^${RoutePathType.WorkflowCLMEvolutionWorkflows}/[^/]+`,
        ).test(activePathname) ||
        new RegExp(
          `^${RoutePathType.WorkflowCLMEvolutionRequestsDetail.replace(':ticketId', '[^/]+')}`,
        ).test(activePathname);
      const hasBothWorkflows = hasBothWorkflowNavEntries(
        currentUser,
        hasClmEvolutionPhase1,
      );
      navLinks.push({
        label: intl.formatMessage(messages.workflowsClmEvolution),
        pathname: permissions['workflowTickets']
          ? RoutePathType.WorkflowCLMEvolutionRequests
          : RoutePathType.WorkflowCLMEvolutionWorkflows,
        activePatterns: ['^/workflows/.+'],
        ...(hasBothWorkflows
          ? {
              badge: intl.formatMessage(messages.workflowsClmEvolutionNewBadge),
            }
          : {}),
        ...(shouldShowSubNav && !isClmEvolutionDetailPage
          ? {
              children: createWorkflowCLMEvolutionSubNavLinks(
                currentUser,
                intl,
                permissions,
              ),
            }
          : {}),
      });
    }

    if (permissions['clauseLibrary']) {
      if (hasClauseLibrary) {
        navLinks.push({
          label: intl.formatMessage({
            id: 'PageHeader.navLinks.clauseLibrary',
            defaultMessage: 'Library',
            description: 'Clause Library navigation link',
          }),
          pathname: hasPlaybooks ? '/playbooks' : '/clause-library',
          children: hasPlaybooks ? createLibrarySubNavLinks(intl) : [],
          activePatterns: hasPlaybooks
            ? [
                '^/playbooks(/.*)?$',
                '^/guidelines(/.*)?$',
                '^/clause-library(/.*)?$',
              ]
            : ['^/clause-library(/.*)?$'],
        });
      }
    }

    if (permissions['documents']) {
      navLinks.push({
        label: intl.formatMessage({
          id: 'PageHeader.navLinks.upload',
          defaultMessage: 'Upload',
          description: 'Upload navigation link',
        }),
        pathname: RoutePathType.Upload,
        children: [],
      });
    }

    const hasAutomationPermission = permissions['automation'];
    const hasDocumentXRayPermission = permissions['documentXRay'];
    const hasWorkflowBuilderPermission = permissions['workflowBuilder'];
    const hasWorkflowRoutingAccess = hasWorkflowBuilderPermission;

    const hasAnyAutomationPermission =
      hasAutomationPermission ||
      hasDocumentXRayPermission ||
      hasWorkflowRoutingAccess;

    if (hasAnyAutomationPermission) {
      const canAccessViaAutomation = canUseAutomation(currentUser);
      const canAccessViaWorkflowBuilder =
        canUseWorkflowAdmin(currentUser) && hasWorkflowBuilderPermission;
      const canAccessAutomation =
        canAccessViaAutomation || canAccessViaWorkflowBuilder;

      // Guided AI / Quick AI clause tagging isn't supported under the
      // Unified Fields data model yet, so the AutomationHub ("Clauses") path
      // isn't a valid landing page while UnifiedFieldsM1 is on — mirrors the
      // sidebar logic in buildSidebarCustomAiNavLink.
      const canAccessAutomationHub =
        hasAutomationPermission && !getFlag(FlagType.UnifiedFieldsM1);
      const canAccessAnyAutomationDestination =
        hasDocumentXRayPermission ||
        canAccessAutomationHub ||
        hasWorkflowRoutingAccess;

      if (canAccessAutomation && canAccessAnyAutomationDestination) {
        const getDefaultPathname = (): string => {
          if (hasDocumentXRayPermission) {
            return RoutePathType.AutomationHubFields;
          }
          if (hasWorkflowRoutingAccess && !canAccessAutomationHub) {
            return RoutePathType.RoutingTable;
          }
          return RoutePathType.AutomationHub;
        };

        const shouldShowSubNavLinks =
          ((hasAutomationPermission && hasDocumentXRayPermission) ||
            hasWorkflowRoutingAccess) &&
          shouldShowAutomationSubNavBar(activePathname);

        navLinks.push({
          label: intl.formatMessage({
            id: 'PageHeader.navLinks.automation',
            defaultMessage: 'Automation',
            description: 'Automation navigation link',
          }),
          pathname: getDefaultPathname(),
          activePatterns: ['^/automation'],
          children: shouldShowSubNavLinks
            ? createAutomationSubNavLinks(
                permissions,
                intl,
                onNavLinkClick,
                hasDynamicDataExtraction,
              )
            : [],
        });
      }
    }

    if (
      checkIsInternalSuperAdmin(currentUser) ||
      featureFlagIncluded(currentUser, 'ADMIN') ||
      checkIsRbacAdmin(currentUser)
    ) {
      navLinks.push({
        label: intl.formatMessage({
          id: 'PageHeader.navLinks.adminConsole',
          defaultMessage: 'Admin',
          description: 'Admin Console navigation link',
        }),
        pathname: `/admin/console/${currentUser.client}`,
        children: [],
      });
    }
  }

  return navLinks;
};

const createWorkflowSubNavLinks = (
  currentUser: User,
  intl: IntlShape,
  permissions: Permissions,
) => {
  const isWorkflowAdmin =
    featureFlagIncluded(currentUser, 'WORKFLOW_DEMO') &&
    currentUser['is_workflow_admin'];

  const showTickets = isWorkflowAdmin || permissions['workflowTickets'];
  const links: NavLink[] = [];

  if (showTickets) {
    links.push({
      label: intl.formatMessage({
        id: 'PageHeader.subNavLinks.workflowTickets',
        defaultMessage: 'Tickets',
        description: 'Workflow Tickets navigation link',
      }),
      pathname: RoutePathType.WorkflowTickets,
      activePatterns: ['^/workflow/tickets/.+'],
    });
  }

  if (permissions.entityGroups) {
    links.push({
      label: intl.formatMessage({
        id: 'PageHeader.subNavLinks.workflowGroups',
        defaultMessage: 'Groups',
        description: 'Workflow Groups navigation link',
      }),
      pathname: RoutePathType.WorkflowGroups,
      activePatterns: ['^/workflow/groups/.+'],
    });
  }

  if (isWorkflowAdmin || permissions['workflowBuilder']) {
    links.push({
      label: intl.formatMessage({
        id: 'PageHeader.subNavLinks.workflowBuilder',
        defaultMessage: 'Workflow Builder',
        description: 'Workflow Builder navigation link',
      }),
      pathname: RoutePathType.WorkflowBuilder,
    });
  }

  return links;
};

const createWorkflowCLMEvolutionSubNavLinks = (
  currentUser: User,
  intl: IntlShape,
  permissions: Permissions,
) => {
  const isWorkflowAdmin =
    featureFlagIncluded(currentUser, 'WORKFLOW_DEMO') &&
    currentUser['is_workflow_admin'];

  const showTickets = isWorkflowAdmin || permissions['workflowTickets'];
  const links: NavLink[] = [];

  if (showTickets) {
    links.push({
      label: intl.formatMessage({
        id: 'PageHeader.subNavLinks.workflowRequests',
        defaultMessage: 'Requests',
        description: 'Workflow Requests navigation link',
      }),
      pathname: RoutePathType.WorkflowCLMEvolutionRequests,
      activePatterns: ['^/workflows/requests/.+'],
    });
  }

  if (isWorkflowAdmin || permissions['workflowBuilder']) {
    links.push({
      label: intl.formatMessage({
        id: 'PageHeader.subNavLinks.workflowCLMEvolutionWorkflows',
        defaultMessage: 'Workflow Builder',
        description: 'Workflow CLM evolution workflow builder navigation link',
      }),
      pathname: RoutePathType.WorkflowCLMEvolutionWorkflows,
      activePatterns: ['^/workflows/workflow-builder/.+'],
    });
  }

  return links;
};

// Builds one sub-nav (Browse) tab per managed entity type, including
// "Groups". `canAccessGroups` lets callers drop the Groups tab for users
// who can't view groups, since its route requires that permission.
const createEntityTypeSubNavLinks = (
  entityTypes: EntityType[],
  canAccessGroups = true,
): NavLink[] => {
  const orderedEntityTypes = [
    ...entityTypes.filter((entityType) => entityType.is_system),
    ...entityTypes.filter((entityType) => !entityType.is_system),
  ];

  return orderedEntityTypes
    .filter(
      (entityType) => !isGroupsSystemEntityType(entityType) || canAccessGroups,
    )
    .map((entityType) => ({
      label: entityType.name,
      pathname: generatePath(RoutePathType.DocumentsEntityType, {
        entityTypeId: entityType.id,
      }),
      // Anchor with `(/|$)` so one entity id that's a prefix of another
      // doesn't mark both tabs active.
      activePatterns: [
        `^/documents/entity-types/${escapeRegExp(entityType.id)}(/|$)`,
      ],
    }));
};

export const createDocumentSubNavLinks = (
  currentUser: User,
  intl: IntlShape,
  entityTypes: EntityType[] = [],
  activePathname = '',
): NavLink[] => {
  if (!shouldShowDocumentSubNavBar(currentUser, activePathname)) {
    return [];
  }

  const allDocumentsLink: NavLink = {
    label: intl.formatMessage({
      id: 'PageHeader.subNavLinks.allDocuments',
      defaultMessage: 'All Documents',
      description: 'All Documents navigation link',
    }),
    pathname: RoutePathType.Documents,
    activePatterns: ['^/documents/\\d+'],
  };

  // One Browse tab per entity type returned by the backend, including Groups.
  return [allDocumentsLink, ...createEntityTypeSubNavLinks(entityTypes)];
};

const createAutomationSubNavLink = (
  {
    label,
    pathname,
    activePatterns,
  }: {
    label: string;
    pathname: RoutePathType;
    activePatterns?: string[];
  },
  onNavLinkClick?: (pathname: string) => void,
): NavLink => ({
  label,
  pathname,
  activePatterns,
  onClick: () => onNavLinkClick?.(pathname),
});

export const createAutomationSubNavLinks = (
  permissions: Permissions,
  intl: IntlShape,
  onNavLinkClick?: (pathname: string) => void,
  hasDynamicDataExtraction = false,
) => {
  const subnavLinks: NavLink[] = [];
  if (permissions['documentXRay']) {
    subnavLinks.push(
      createAutomationSubNavLink(
        {
          label: intl.formatMessage({
            id: 'PageHeader.subNavLinks.fields',
            defaultMessage: 'Fields',
            description: 'Fields automation navigation link',
          }),
          pathname: RoutePathType.AutomationHubFields,
          activePatterns: ['^/automation/fields'],
        },
        onNavLinkClick,
      ),
    );
  }
  // Guided AI / Quick AI clause tagging isn't supported under the Unified
  // Fields data model yet, so hide the Clauses subtab while UnifiedFieldsM1 is on.
  if (permissions['automation'] && !getFlag(FlagType.UnifiedFieldsM1)) {
    subnavLinks.push(
      createAutomationSubNavLink(
        {
          label: intl.formatMessage({
            id: 'PageHeader.subNavLinks.automation',
            defaultMessage: 'Clauses',
            description: 'Clause automation navigation link',
          }),
          pathname: RoutePathType.AutomationHub,
          activePatterns: ['^/automation$', '^/automation/detail'],
        },
        onNavLinkClick,
      ),
    );
  }
  if (permissions['workflowBuilder']) {
    subnavLinks.push(
      createAutomationSubNavLink(
        {
          label: intl.formatMessage({
            id: 'PageHeader.subNavLinks.routingTable',
            defaultMessage: 'Routing Table',
            description: 'Routing Table navigation link',
          }),
          pathname: RoutePathType.RoutingTable,
          activePatterns: ['^/automation/routing-table'],
        },
        onNavLinkClick,
      ),
    );
    if (hasDynamicDataExtraction) {
      subnavLinks.push(
        createAutomationSubNavLink(
          {
            label: intl.formatMessage({
              id: 'PageHeader.subNavLinks.dataExtraction',
              defaultMessage: 'Data Extraction',
              description: 'Data Extraction automation navigation link',
            }),
            pathname: RoutePathType.DynamicDataExtraction,
            activePatterns: ['^/automation/dynamic-data-extraction$'],
          },
          onNavLinkClick,
        ),
      );
    }
  }
  return subnavLinks;
};

export const createSearchSubNavLinks = ({
  intl,
  activeSearch,
  hasDeprecateAnalyzer,
  onNavLinkClick,
}: {
  intl: IntlShape;
  activeSearch?: SavedSearch;
  hasDeprecateAnalyzer?: boolean;
  onNavLinkClick?: (pathname: string) => void;
}): NavLink[] => {
  const searchSubNavs: NavLink[] = [
    {
      label: intl.formatMessage({
        id: 'PageHeader.subNavLinks.search',
        defaultMessage: 'Search',
        description: 'Search navigation link',
      }),
      pathname: RoutePathType.Search,
      onClick: () => onNavLinkClick?.(RoutePathType.Search),
    },

    ...(hasDeprecateAnalyzer
      ? []
      : [
          {
            label: intl.formatMessage({
              id: 'PageHeader.subNavLinks.analyzer',
              defaultMessage: 'Analyzer',
              description: 'Analyzer navigation link',
            }),
            pathname: RoutePathType.Analyzer,
            onClick: () => onNavLinkClick?.(RoutePathType.Analyzer),
          },
        ]),

    {
      label: intl.formatMessage({
        id: 'PageHeader.subNavLinks.savedSearches',
        defaultMessage: 'Saved Searches',
        description: 'Saved Searches navigation link',
      }),
      pathname: RoutePathType.SavedSearches,
      onClick: () => onNavLinkClick?.(RoutePathType.SavedSearches),
    },
  ];

  if (activeSearch) {
    searchSubNavs.push({
      label: activeSearch.name,
      pathname: RoutePathType.SavedSearch.replace(
        ':searchId',
        `${activeSearch.id}`,
      ),
      onClick: () => {},
    });
  }

  if (!getFlag(FlagType.UnifiedFieldsM1)) {
    searchSubNavs.push({
      label: intl.formatMessage({
        id: 'PageHeader.subNavLinks.alerts',
        defaultMessage: 'Alerts',
        description: 'Alerts navigation link',
      }),
      pathname: RoutePathType.Alerts,
    });
  }
  return searchSubNavs;
};

const createDashboardsSubNavLinks = (
  _currentUser: User,
  _pathname: string,
  intl: IntlShape,
  dashboards?: any[],
  onNavLinkClick?: (pathname: string) => void,
  activeDashboard?: DashboardAndPathname,
): NavLink[] => {
  const dashboardSubNavs = (dashboards || []).map((link: DashboardTab) => {
    const pathname = link.is_default
      ? `/dashboards/${link.id}`
      : `/dashboards/${link.type}`;

    const isActive = pathname === activeDashboard?.pathname;
    return {
      label: link.name,
      onClick: isActive ? undefined : () => onNavLinkClick?.(pathname),
      pathname,
    };
  });

  dashboardSubNavs.push({
    label: intl.formatMessage({
      id: 'PageHeader.subNavLinks.savedDashboards',
      defaultMessage: 'Saved Dashboards',
      description: 'Saved Dashboards navigation link',
    }),
    onClick: () => onNavLinkClick?.(RoutePathType.SavedDashboards),
    pathname: RoutePathType.SavedDashboards,
  });

  if (
    activeDashboard &&
    activeDashboard.name &&
    shouldShowSavedDashboardLink(activeDashboard)
  ) {
    dashboardSubNavs.push({
      label: activeDashboard.name,
      pathname: activeDashboard.pathname,
      onClick: () => {},
    });
  }
  return dashboardSubNavs as NavLink[];
};

const lookerDashboardPagePattern = /(documents|expirations|tickets|workflows)/g;

const createLibrarySubNavLinks = (intl: IntlShape): NavLink[] => {
  return [
    {
      label: intl.formatMessage({
        id: 'PageHeader.subNavLinks.playbooks',
        defaultMessage: 'Playbooks',
        description: 'Playbooks navigation link',
      }),
      pathname: '/playbooks',
    },
    {
      label: intl.formatMessage({
        id: 'PageHeader.subNavLinks.aiInstructions',
        defaultMessage: 'Negotiation Requirements',
        description: 'Negotiation Requirements navigation link',
      }),
      pathname: '/guidelines',
    },
    {
      label: intl.formatMessage({
        id: 'PageHeader.subNavLinks.clauses',
        defaultMessage: 'Clauses',
        description: 'Clauses navigation link',
      }),
      pathname: '/clause-library',
    },
  ];
};

const shouldShowSavedDashboardLink = (activeDashboard: DashboardAndPathname) =>
  activeDashboard?.pathname.includes('dashboards/') &&
  !activeDashboard?.pathname.match(lookerDashboardPagePattern) &&
  !activeDashboard.is_default;

export const shouldShowDocumentSubNavBar = (
  currentUser: User,
  pathname: string,
) =>
  !featureFlagIncluded(currentUser, 'WORKFLOW') &&
  pathname.startsWith(RoutePathType.Documents);

export const shouldShowAutomationSubNavBar = (pathname: string) =>
  !pathname.startsWith(
    RoutePathType.ViewDataExtraction.replace('/:extractionId', ''),
  );

const isSearchPathname = (pathname: string) =>
  pathname.startsWith(RoutePathType.Search) ||
  pathname.startsWith(RoutePathType.Analyzer) ||
  pathname.startsWith(RoutePathType.SavedSearches) ||
  pathname.startsWith(RoutePathType.DownloadExportReportPage);

export const shouldShowSearchSubNavbar = (pathname: string) =>
  isSearchPathname(pathname);

interface SidebarDocumentsSubNavLinksOptions {
  intl: IntlShape;
  permissions: Permissions;
  entityTypes: EntityType[];
}

const createSidebarDocumentsSubNavLinks = ({
  intl,
  permissions,
  entityTypes,
}: SidebarDocumentsSubNavLinksOptions): NavLink[] => {
  const links: NavLink[] = [];

  if (permissions.documents) {
    links.push({
      label: intl.formatMessage({
        id: 'PageHeader.subNavLinks.allDocuments',
        defaultMessage: 'All Documents',
        description: 'All Documents navigation link',
      }),
      pathname: RoutePathType.Documents,
      activePatterns: [String.raw`^/documents/\d+`],
    });

    // One dynamic Browse tab per entity type (Groups reuses the groups route).
    // Not gated on the MesParties flag: general clients get only the seeded
    // "Groups" system type; parties-enabled clients get the extra types.
    // `permissions.entityGroups` is itself flag-aware (entity_groups pre-flag,
    // entities/entity_types post-flag, EKP-59166) so we don't surface a tab
    // that dead-ends at the route gate.
    const canAccessGroups = !!permissions.entityGroups;
    links.push(...createEntityTypeSubNavLinks(entityTypes, canAccessGroups));
  }

  return links;
};

const createSidebarLibrarySubNavLinks = (intl: IntlShape): NavLink[] => [
  {
    label: intl.formatMessage({
      id: 'PageHeader.subNavLinks.playbooks',
      defaultMessage: 'Playbooks',
      description: 'Playbooks navigation link',
    }),
    pathname: '/playbooks',
  },
  {
    label: intl.formatMessage({
      id: 'PageHeader.subNavLinks.aiInstructions',
      defaultMessage: 'Negotiation Requirements',
      description: 'Negotiation Requirements navigation link',
    }),
    pathname: '/guidelines',
  },
  {
    label: intl.formatMessage({
      id: 'PageHeader.subNavLinks.clauses',
      defaultMessage: 'Clauses',
      description: 'Clauses navigation link',
    }),
    pathname: '/clause-library',
  },
];

export const createCustomAiSubNavLinks = (
  permissions: Permissions,
  intl: IntlShape,
): NavLink[] => {
  const links: NavLink[] = [];

  if (permissions.documentXRay) {
    links.push({
      label: intl.formatMessage({
        id: 'PageHeader.subNavLinks.fields',
        defaultMessage: 'Fields',
        description: 'Fields automation navigation link',
      }),
      pathname: RoutePathType.AutomationHubFields,
    });
  }

  // Guided AI / Quick AI clause tagging isn't supported under the Unified
  // Fields data model yet, so hide the Clauses subtab while UnifiedFieldsM1 is on.
  if (permissions.automation && !getFlag(FlagType.UnifiedFieldsM1)) {
    links.push({
      label: intl.formatMessage({
        id: 'PageHeader.subNavLinks.automation',
        defaultMessage: 'Clauses',
        description: 'Clause automation navigation link',
      }),
      pathname: RoutePathType.AutomationHub,
    });
  }

  // Routing Table configures how both legacy Tickets and CLM Evolution
  // Requests get routed — it isn't tied to either Workflows experience's UI,
  // so it lives here under Custom AI instead.
  if (permissions.workflowBuilder) {
    links.push({
      label: intl.formatMessage({
        id: 'PageHeader.subNavLinks.routingTable',
        defaultMessage: 'Routing Table',
        description: 'Routing Table navigation link',
      }),
      pathname: RoutePathType.RoutingTable,
    });
  }

  return links;
};

interface SidebarNavlinksOptions {
  currentUser: User;
  intl: IntlShape;
  onNavLinkClick: (pathname: string) => void;
  dashboards?: DashboardTab[];
  activeSearch?: SavedSearch;
  hasClauseLibrary?: boolean;
  hasDeprecateAnalyzer?: boolean;
  hasPlaybooks?: boolean;
  activeDashboard?: DashboardAndPathname;
  hasClmEvolutionPhase1?: boolean;
  permissions?: Permissions;
  activePathname?: string;
  entityTypes?: EntityType[];
}

const resolveWorkflowNavPathname = ({
  hasClmEvolution,
  hasTicketsPermission,
}: {
  hasClmEvolution: boolean;
  hasTicketsPermission: boolean;
}) => {
  if (hasClmEvolution && hasTicketsPermission) {
    return RoutePathType.WorkflowCLMEvolutionRequests;
  }
  if (hasClmEvolution) {
    return RoutePathType.WorkflowCLMEvolutionWorkflows;
  }
  if (hasTicketsPermission) {
    return RoutePathType.WorkflowTickets;
  }
  return RoutePathType.WorkflowBuilder;
};

// Legacy "Workflow" — its own top-level row, separate from CLM Evolution's
// "Workflows" row below. Only shown for clients that still have the legacy
// experience; net-new CLM Evolution clients get only the row below.
const buildSidebarWorkflowNavLink = (
  options: SidebarNavlinksOptions,
): NavLink | null => {
  const { currentUser, intl, permissions = {} } = options;

  if (!permissions.workflowTickets && !permissions.workflowBuilder) {
    return null;
  }

  if (!featureFlagIncluded(currentUser, 'WORKFLOW_DEMO')) {
    return null;
  }

  // Groups nests under Documents in the sidebar (see
  // createSidebarDocumentsSubNavLinks), so drop the legacy nav's Groups child.
  const children = createWorkflowSubNavLinks(
    currentUser,
    intl,
    permissions,
  ).filter((link) => link.pathname !== RoutePathType.WorkflowGroups);

  return {
    label: intl.formatMessage({
      id: 'PageHeader.navLinks.workflow',
      defaultMessage: 'Workflows',
      description: 'Workflow navigation link',
    }),
    pathname: permissions.workflowTickets
      ? RoutePathType.WorkflowTickets
      : RoutePathType.WorkflowBuilder,
    activePatterns: ['^/workflow/.+'],
    ...(children.length > 1 ? { children } : {}),
  };
};

// CLM Evolution — its own top-level "Workflows" row. Gets the "New" badge
// only while the legacy row above also renders, for clients transitioning
// between the two experiences.
const buildSidebarWorkflowCLMEvolutionNavLink = (
  options: SidebarNavlinksOptions,
): NavLink | null => {
  const {
    currentUser,
    intl,
    hasClmEvolutionPhase1,
    permissions = {},
  } = options;

  if (!permissions.workflowTickets && !permissions.workflowBuilder) {
    return null;
  }

  if (!hasClmEvolutionPhase1) {
    return null;
  }

  const hasBothWorkflowExperiences = featureFlagIncluded(
    currentUser,
    'WORKFLOW_DEMO',
  );

  const children = createWorkflowCLMEvolutionSubNavLinks(
    currentUser,
    intl,
    permissions,
  );

  return {
    label: intl.formatMessage(messages.workflowsClmEvolution),
    pathname: resolveWorkflowNavPathname({
      hasClmEvolution: true,
      hasTicketsPermission: !!permissions.workflowTickets,
    }),
    activePatterns: ['^/workflows/.+'],
    ...(hasBothWorkflowExperiences
      ? { badge: intl.formatMessage(messages.workflowsClmEvolutionNewBadge) }
      : {}),
    ...(children.length > 1 ? { children } : {}),
  };
};

// Groups nests under Documents as an entity-type Browse tab (see
// createSidebarDocumentsSubNavLinks) — never under Workflow. This is a
// fallback top-level link for users who can see Groups but not Documents,
// without which they'd lose access to Groups entirely.
const buildSidebarGroupsNavLink = (
  options: SidebarNavlinksOptions,
): NavLink | null => {
  const { intl, permissions = {} } = options;

  if (permissions.documents || !permissions.entityGroups) {
    return null;
  }

  return {
    label: intl.formatMessage({
      id: 'SidebarNav.workflowGroups',
      defaultMessage: 'Groups',
      description:
        'Top-level Groups navigation link in the sidebar, shown when Workflow is not visible',
    }),
    pathname: RoutePathType.WorkflowGroups,
    activePatterns: ['^/workflow/groups/.+'],
  };
};

const buildSidebarDocumentsNavLink = (
  options: SidebarNavlinksOptions,
): NavLink | null => {
  const { intl, permissions = {}, entityTypes } = options;

  if (!permissions.documents) return null;

  const documentsChildren = createSidebarDocumentsSubNavLinks({
    intl,
    permissions,
    entityTypes: entityTypes ?? [],
  });

  return {
    label: intl.formatMessage({
      id: 'PageHeader.navLinks.documents',
      defaultMessage: 'Documents',
      description: 'Documents navigation link',
    }),
    pathname: RoutePathType.Documents,
    activePatterns: ['^/documents(/.*)?$'],
    children: documentsChildren,
  };
};

const buildSidebarDashboardsNavLink = (
  options: SidebarNavlinksOptions,
): NavLink | null => {
  const {
    currentUser,
    intl,
    dashboards,
    onNavLinkClick,
    activeDashboard,
    permissions = {},
  } = options;

  if (!permissions.documentsDashboard && !permissions.workflowsDashboard) {
    return null;
  }

  return {
    label: intl.formatMessage({
      id: 'PageHeader.navLinks.dashboards',
      defaultMessage: 'Dashboards',
      description: 'Dashboards navigation link',
    }),
    pathname: RoutePathType.Dashboards,
    activePatterns: ['^/dashboards(/.*)?$', '^/saved-dashboards(/.*)?$'],
    children: createDashboardsSubNavLinks(
      currentUser,
      RoutePathType.Dashboards,
      intl,
      dashboards,
      onNavLinkClick,
      activeDashboard,
    ),
  };
};

const buildSidebarSearchNavLink = (
  options: SidebarNavlinksOptions,
): NavLink | null => {
  const {
    intl,
    onNavLinkClick,
    activeSearch,
    hasDeprecateAnalyzer,
    permissions = {},
  } = options;

  if (!permissions.documents) return null;

  return {
    label: intl.formatMessage({
      id: 'PageHeader.navLinks.search',
      defaultMessage: 'Search',
      description: 'Search navigation link',
    }),
    pathname: RoutePathType.Search,
    children: createSearchSubNavLinks({
      activeSearch,
      hasDeprecateAnalyzer,
      onNavLinkClick,
      intl,
    }),
    onClick: () => onNavLinkClick(RoutePathType.Search),
  };
};

const buildSidebarClauseLibraryNavLink = (
  options: SidebarNavlinksOptions,
): NavLink | null => {
  const { intl, hasClauseLibrary, hasPlaybooks, permissions = {} } = options;

  if (!permissions.clauseLibrary || !hasClauseLibrary) return null;

  return {
    label: intl.formatMessage({
      id: 'PageHeader.navLinks.clauseLibrary',
      defaultMessage: 'Library',
      description: 'Clause Library navigation link',
    }),
    pathname: hasPlaybooks ? '/playbooks' : '/clause-library',
    children: hasPlaybooks ? createSidebarLibrarySubNavLinks(intl) : [],
    activePatterns: hasPlaybooks
      ? ['^/playbooks(/.*)?$', '^/guidelines(/.*)?$', '^/clause-library(/.*)?$']
      : ['^/clause-library(/.*)?$'],
  };
};

const resolveCustomAiNavPathname = ({
  hasDocumentXRayAccess,
  hasAutomationHubAccess,
}: {
  hasDocumentXRayAccess: boolean;
  hasAutomationHubAccess: boolean;
}) => {
  if (hasDocumentXRayAccess) {
    return RoutePathType.AutomationHubFields;
  }
  if (hasAutomationHubAccess) {
    return RoutePathType.AutomationHub;
  }
  // Only Routing Table access (workflowBuilder-only, no Fields/Clauses) lands
  // there directly.
  return RoutePathType.RoutingTable;
};

const buildSidebarCustomAiNavLink = (
  options: SidebarNavlinksOptions,
): NavLink | null => {
  const { currentUser, intl, permissions = {} } = options;

  const hasAnyCustomAiPermission =
    permissions.automation ||
    permissions.documentXRay ||
    permissions.workflowBuilder;
  if (!hasAnyCustomAiPermission) return null;

  const canAccessCustomAi =
    canUseAutomation(currentUser) ||
    (canUseWorkflowAdmin(currentUser) && !!permissions.workflowBuilder);
  if (!canAccessCustomAi) return null;

  // Guided AI / Quick AI clause tagging isn't supported under the Unified
  // Fields data model yet, so the parent link shouldn't default to the
  // disabled AutomationHub when only the (hidden) Clauses subtab would have
  // pointed there.
  const canAccessAutomationHub =
    permissions.automation && !getFlag(FlagType.UnifiedFieldsM1);
  if (
    !permissions.documentXRay &&
    !canAccessAutomationHub &&
    !permissions.workflowBuilder
  ) {
    return null;
  }

  return {
    label: intl.formatMessage({
      id: 'SidebarNav.customAi',
      defaultMessage: 'Custom AI',
      description: 'Custom AI navigation link in the sidebar',
    }),
    pathname: resolveCustomAiNavPathname({
      hasDocumentXRayAccess: !!permissions.documentXRay,
      hasAutomationHubAccess: !!canAccessAutomationHub,
    }),
    activePatterns: ['^/automation(/.*)?$'],
    children: createCustomAiSubNavLinks(permissions, intl),
  };
};

export const buildSidebarAdminNavLink = (
  currentUser: User,
  intl: IntlShape,
): NavLink | null => {
  const isAdmin =
    checkIsInternalSuperAdmin(currentUser) ||
    featureFlagIncluded(currentUser, 'ADMIN') ||
    checkIsRbacAdmin(currentUser);

  if (!isAdmin) return null;

  return {
    label: intl.formatMessage({
      id: 'PageHeader.navLinks.adminConsole',
      defaultMessage: 'Admin',
      description: 'Admin Console navigation link',
    }),
    pathname: `/admin/console/${currentUser.client}`,
    children: [],
  };
};

export const createSidebarNavlinks = (
  options: SidebarNavlinksOptions,
): NavLink[] => {
  const { currentUser, intl } = options;

  if (checkIsWorkflowOnly(currentUser)) {
    if (featureFlagIncluded(currentUser, 'WORKFLOW_DEMO')) {
      return [
        {
          label: intl.formatMessage({
            id: 'PageHeader.navLinks.workflowTickets',
            defaultMessage: 'Workflows',
            description: 'Workflow navigation link',
          }),
          pathname: RoutePathType.WorkflowTickets,
          children: [],
        },
      ];
    }
    return [];
  }

  if (featureFlagIncluded(currentUser, 'REVIEWER')) {
    return [];
  }

  return [
    buildSidebarWorkflowNavLink(options),
    buildSidebarWorkflowCLMEvolutionNavLink(options),
    buildSidebarDocumentsNavLink(options),
    buildSidebarGroupsNavLink(options),
    buildSidebarDashboardsNavLink(options),
    buildSidebarSearchNavLink(options),
    buildSidebarClauseLibraryNavLink(options),
    buildSidebarCustomAiNavLink(options),
  ].filter((link): link is NavLink => link !== null);
};
